import { useIntl } from "react-intl";

import { TriButton } from "~/components/ui/tri-button";
import { useSite } from "~/contexts";
import { ClaimCard } from "~/hec/components/molecules/claim-card/claim-card";
import { claimCtaClassName } from "~/hec/components/molecules/claim-card/claim-cta-class";
import { ClaimResponseMessage } from "~/hec/components/molecules/claim-response-message/claim-response-message";
import { ContributionAmountField } from "~/hec/components/molecules/contribution-amount-field/contribution-amount-field";
import { EnrollCta } from "~/hec/components/molecules/enroll-cta/enroll-cta";
import { FuturePlansField } from "~/hec/components/molecules/future-plans-field/future-plans-field";
import { SignInCta } from "~/hec/components/molecules/sign-in-cta/sign-in-cta";
import { useAuth } from "~/hec/context/auth-context";
import type {
  ClaimStatus,
  ClaimValues,
  ClaimVariant,
} from "~/hec/loyalty/types";
import { useInsidersClaim } from "~/hec/loyalty/use-insiders-claim";
import { useRootLayoutData } from "~/root";

import {
  EDITOR_SAMPLE_ERROR,
  EDITOR_SAMPLE_SUCCESS,
  resolveInsidersClaimCopy,
} from "./copy";
import messages from "./messages";
import type {
  InsidersClaimEditorState,
  InsidersClaimFormProps,
  ResolvedInsidersClaimCopy,
} from "./types";

/**
 * Organism: one Insiders Perks claim card (COM-506) — the React port of the
 * SFRA `Loyalty-Form` include + `loyalty.js`. Composes the claim-card
 * molecules around `useInsidersClaim`, which owns every SFRA call.
 *
 * Behaviour deltas vs SFRA, all deliberate:
 *  - guests see a "Sign in to claim" CTA instead of a failed claim (BAC 7);
 *  - non-members see an "Enroll now" CTA instead of Annex's raw not-enrolled
 *    reply, which SFRA leaks verbatim (COM-506 review, 2026-10-07);
 *  - the button is disabled while a claim is in flight (BAC 8);
 *  - a stale CSRF token is refreshed and the claim retried once;
 *  - each card has unique control ids (`useId`), where SFRA reused one.
 */
const EDITOR_STATUS: Record<InsidersClaimEditorState, ClaimStatus> = {
  ready: { kind: "ready" },
  guest: { kind: "guest" },
  notEnrolled: { kind: "notEnrolled" },
  success: { kind: "succeeded", message: EDITOR_SAMPLE_SUCCESS },
  error: { kind: "failed", message: EDITOR_SAMPLE_ERROR, format: "text" },
};

const isBusy = (status: ClaimStatus): boolean =>
  status.kind === "loading" ||
  status.kind === "submitting" ||
  status.kind === "unavailable";

interface ClaimFieldsProps {
  variant: ClaimVariant;
  copy: ResolvedInsidersClaimCopy;
  values: ClaimValues;
  disabled: boolean;
  onAmountChange: (amount: string) => void;
  onAnswerChange: (answer: boolean) => void;
}

function ClaimFields({
  variant,
  copy,
  values,
  disabled,
  onAmountChange,
  onAnswerChange,
}: ClaimFieldsProps) {
  if (variant === "annualContribution") {
    return (
      <ContributionAmountField
        label={copy.inputLabel}
        value={values.amount}
        onChange={onAmountChange}
        disabled={disabled}
        data-testid="loyalty-contribution-input"
      />
    );
  }
  if (variant === "futurePlans") {
    return (
      <FuturePlansField
        value={values.answer}
        onChange={onAnswerChange}
        yesLabel={copy.yesLabel}
        noLabel={copy.noLabel}
        disabled={disabled}
        data-testid="loyalty-future-plans-radio"
      />
    );
  }
  return null;
}

interface ClaimMessageProps {
  status: ClaimStatus;
  variant: ClaimVariant;
  genericFailure: string;
  enrollToClaim: string;
}

function ClaimMessage({
  status,
  variant,
  genericFailure,
  enrollToClaim,
}: ClaimMessageProps) {
  const testId = `loyalty-response-message-${variant}`;
  if (status.kind === "succeeded") {
    return (
      <ClaimResponseMessage
        tone="success"
        format="html"
        message={status.message}
        data-testid={testId}
      />
    );
  }
  if (status.kind === "failed") {
    return (
      <ClaimResponseMessage
        tone="error"
        format={status.message === null ? "text" : status.format}
        message={status.message ?? genericFailure}
        data-testid={testId}
      />
    );
  }
  if (status.kind === "unavailable") {
    return (
      <ClaimResponseMessage
        tone="error"
        format="text"
        message={genericFailure}
        data-testid={testId}
      />
    );
  }
  if (status.kind === "notEnrolled") {
    // Not an error — nothing failed, the shopper just isn't a member yet.
    // `success` is what gives this `role="status"` rather than `role="alert"`
    // (tone picks the ARIA role only; it carries no colour), so the CTA
    // swapping under a shopper who just clicked Claim is announced.
    return (
      <ClaimResponseMessage
        tone="success"
        format="text"
        message={enrollToClaim}
        data-testid={testId}
      />
    );
  }
  return null;
}

export function InsidersClaimForm({
  variant,
  theme = "light",
  copy,
  editorState,
  className,
  ...rest
}: InsidersClaimFormProps) {
  const intl = useIntl();
  const { site } = useSite();
  const { isLoggedIn } = useAuth();
  // Perks opt-in, already parsed from the persisted `c_acLoyaltyData` by the
  // root loader — the same source the goal banner reads. No Annex call, and a
  // revalidation after an in-page enroll re-derives it.
  const { customerLoyalty } = useRootLayoutData();
  const inEditor = editorState !== undefined;
  const claim = useInsidersClaim({
    variant,
    siteId: site.id,
    enabled: !inEditor,
    isLoggedIn,
    isEnrolled: customerLoyalty,
  });
  const status = inEditor ? EDITOR_STATUS[editorState] : claim.status;
  const text = resolveInsidersClaimCopy(copy, variant, site.id);
  const busy = isBusy(status);
  const buttonVariant = theme === "dark" ? "secondary" : "cta";

  const cta =
    status.kind === "guest" ? (
      <SignInCta
        label={intl.formatMessage(messages.signInToClaim)}
        variant={buttonVariant}
        data-testid={`loyalty-sign-in-link-${variant}`}
      />
    ) : status.kind === "notEnrolled" ? (
      // No Claim button: without a Perks membership the claim cannot succeed.
      <EnrollCta
        label={intl.formatMessage(messages.enrollNow)}
        variant={buttonVariant}
        data-testid={`loyalty-enroll-link-${variant}`}
      />
    ) : (
      <TriButton
        type="button"
        variant={buttonVariant}
        size="fit"
        className={claimCtaClassName}
        disabled={busy}
        isLoading={status.kind === "submitting"}
        succeeded={
          status.kind === "succeeded"
            ? true
            : status.kind === "failed"
              ? false
              : undefined
        }
        loadingContent={text.buttonLabel}
        onClick={() => void claim.submit()}
        data-testid={`loyalty-claim-button-${variant}`}
      >
        {text.buttonLabel}
      </TriButton>
    );

  return (
    <ClaimCard
      data-testid={`loyalty-claim-card-${variant}`}
      eyebrow={text.eyebrow}
      heading={text.heading}
      bodyHtml={text.body}
      theme={theme}
      cta={cta}
      message={
        <ClaimMessage
          status={status}
          variant={variant}
          genericFailure={intl.formatMessage(messages.genericFailure)}
          enrollToClaim={intl.formatMessage(messages.enrollToClaim)}
        />
      }
      className={className}
      {...rest}
    >
      <ClaimFields
        variant={variant}
        copy={text}
        values={claim.values}
        disabled={busy}
        onAmountChange={claim.setAmount}
        onAnswerChange={claim.setAnswer}
      />
    </ClaimCard>
  );
}
